import React, { useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Trophy, Clock, Target, TrendingUp, Award } from 'lucide-react';

function formatTime(seconds) {
  if (!seconds) return '-';
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}m ${secs}s`;
}

function formatDate(dateStr) {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

export function LeaderboardModal({ open, onOpenChange, getChallengeStats }) {
  const stats = useMemo(() => getChallengeStats(), [getChallengeStats, open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white border-gray-200 text-black max-w-md rounded-lg" data-testid="leaderboard-modal">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-center flex items-center justify-center gap-2">
            <Trophy className="w-5 h-5 text-yellow-500" />
            Challenge Leaderboard
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-5">
          {stats.totalChallenges === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Trophy className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="font-medium">No challenges played yet</p>
              <p className="text-sm mt-1">Complete some challenges to see your stats!</p>
            </div>
          ) : (
            <>
              {/* Stats Grid */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-gray-50 rounded-lg p-3 text-center">
                  <div className="flex items-center justify-center gap-1 text-gray-500 text-xs mb-1">
                    <Target className="w-3 h-3" />
                    Win Rate
                  </div>
                  <div className="text-2xl font-bold text-[#6aaa64]">{stats.winRate}%</div>
                  <div className="text-xs text-gray-400">{stats.wins}/{stats.totalChallenges}</div>
                </div>
                
                <div className="bg-gray-50 rounded-lg p-3 text-center">
                  <div className="flex items-center justify-center gap-1 text-gray-500 text-xs mb-1">
                    <TrendingUp className="w-3 h-3" />
                    Avg Guesses
                  </div>
                  <div className="text-2xl font-bold">{stats.avgGuesses || '-'}</div>
                  <div className="text-xs text-gray-400">when winning</div>
                </div>
                
                <div className="bg-gray-50 rounded-lg p-3 text-center">
                  <div className="flex items-center justify-center gap-1 text-gray-500 text-xs mb-1">
                    <Award className="w-3 h-3" />
                    Best Score
                  </div>
                  <div className="text-2xl font-bold text-[#c9b458]">
                    {stats.bestGuesses ? `${stats.bestGuesses}/6` : '-'}
                  </div>
                  <div className="text-xs text-gray-400">guesses</div>
                </div>
                
                <div className="bg-gray-50 rounded-lg p-3 text-center">
                  <div className="flex items-center justify-center gap-1 text-gray-500 text-xs mb-1">
                    <Clock className="w-3 h-3" />
                    Best Time
                  </div>
                  <div className="text-2xl font-bold text-blue-500">
                    {formatTime(stats.bestTime)}
                  </div>
                  <div className="text-xs text-gray-400">to solve</div>
                </div>
              </div>

              {/* Recent Games */}
              {stats.recentGames.length > 0 && (
                <div>
                  <h3 className="text-sm font-bold mb-2 text-gray-700">Recent Challenges</h3>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {stats.recentGames.map((game, i) => (
                      <div 
                        key={i}
                        className={`flex items-center justify-between px-3 py-2 rounded-lg text-sm ${
                          game.success ? 'bg-green-50' : 'bg-red-50'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <span className={`font-mono font-bold ${game.success ? 'text-[#6aaa64]' : 'text-red-500'}`}>
                            {game.word}
                          </span>
                          <span className="text-gray-400 text-xs">
                            {formatDate(game.date)}
                          </span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className={`font-medium ${game.success ? 'text-[#6aaa64]' : 'text-red-500'}`}>
                            {game.success ? `${game.guesses}/6` : 'X'}
                          </span>
                          {game.timeTaken && (
                            <span className="text-gray-400 text-xs">
                              {formatTime(game.timeTaken)}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
